/**
 * Python Quick Reference data for ICSE/ISC Computer Class XI & XII chapters.
 * Each topic_id maps to a list of method/concept rows.
 * Shown at the bottom of the MaterialsPage for any Python chapter.
 */

const STR_METHODS = [
  { name: "len(s)", desc: "Number of characters in string s.", ex: 'len("brimi")  →  5' },
  { name: "s.upper()", desc: "All uppercase.", ex: '"hi".upper()  →  "HI"' },
  { name: "s.lower()", desc: "All lowercase.", ex: '"HI".lower()  →  "hi"' },
  { name: "s.capitalize()", desc: "First letter uppercase, rest lowercase.", ex: '"brimi ai".capitalize()  →  "Brimi ai"' },
  { name: "s.title()", desc: "Title Case — first letter of each word uppercase.", ex: '"brimi ai".title()  →  "Brimi Ai"' },
  { name: "s.count(sub)", desc: "How many times sub appears in s.", ex: '"banana".count("a")  →  3' },
  { name: "s.find(sub)", desc: "Index of first occurrence (or -1).", ex: '"brimi".find("m")  →  3' },
  { name: "s.index(sub)", desc: "Like find(), but raises ValueError if not found.", ex: '"brimi".index("i")  →  2' },
  { name: "s.startswith(p)", desc: "True if s starts with prefix p.", ex: '"brimi.in".startswith("brimi")  →  True' },
  { name: "s.endswith(suf)", desc: "True if s ends with suffix suf.", ex: '"brimi.in".endswith(".in")  →  True' },
  { name: "s.isalpha()", desc: "True if all chars are letters.", ex: '"abc".isalpha()  →  True' },
  { name: "s.isdigit()", desc: "True if all chars are digits.", ex: '"123".isdigit()  →  True' },
  { name: "s.isalnum()", desc: "True if all chars are letters/digits.", ex: '"ab12".isalnum()  →  True' },
  { name: "s.isupper() / s.islower()", desc: "True if all alphabetic chars are upper/lower.", ex: '"BRIMI".isupper()  →  True' },
  { name: "s.isspace()", desc: "True if string only contains whitespace.", ex: '"   ".isspace()  →  True' },
  { name: "s.strip()", desc: "Remove leading & trailing whitespace.", ex: '"  hi  ".strip()  →  "hi"' },
  { name: "s.lstrip() / s.rstrip()", desc: "Remove only leading / trailing whitespace.", ex: '"  hi".lstrip()  →  "hi"' },
  { name: "s.replace(a, b)", desc: "Replace every a with b.", ex: '"brimi".replace("i", "y")  →  "brymy"' },
  { name: "sep.join(list)", desc: "Joins list items with sep between them.", ex: '"-".join(["a","b","c"])  →  "a-b-c"' },
  { name: "s.split(sep)", desc: "Split into list at sep.", ex: '"a,b,c".split(",")  →  ["a","b","c"]' },
  { name: "s.partition(sep)", desc: "Split into (before, sep, after).", ex: '"a=1".partition("=")  →  ("a","=","1")' },
];

const LIST_METHODS = [
  { name: "len(L)", desc: "Number of elements.", ex: 'len([1,2,3])  →  3' },
  { name: "list(it)", desc: "Convert iterable to list.", ex: 'list("ab")  →  ["a","b"]' },
  { name: "L.append(x)", desc: "Add x to end (in place).", ex: 'L = [1,2]; L.append(3)  →  [1,2,3]' },
  { name: "L.extend(it)", desc: "Append all items from iterable.", ex: '[1,2].extend([3,4])  →  [1,2,3,4]' },
  { name: "L.insert(i, x)", desc: "Insert x at index i.", ex: 'L=[1,3]; L.insert(1, 2)  →  [1,2,3]' },
  { name: "L.count(x)", desc: "How many times x appears.", ex: '[1,2,1].count(1)  →  2' },
  { name: "L.index(x)", desc: "Index of first x. Raises ValueError if missing.", ex: '[10,20,30].index(20)  →  1' },
  { name: "L.remove(x)", desc: "Remove first occurrence of x.", ex: 'L=[1,2,3]; L.remove(2)  →  [1,3]' },
  { name: "L.pop([i])", desc: "Remove & return item at index i (last by default).", ex: '[1,2,3].pop()  →  3' },
  { name: "L.reverse()", desc: "Reverse in place.", ex: 'L=[1,2,3]; L.reverse()  →  [3,2,1]' },
  { name: "L.sort()", desc: "Sort in place.", ex: '[3,1,2].sort()  →  [1,2,3]' },
  { name: "sorted(L)", desc: "Return a NEW sorted list.", ex: 'sorted([3,1,2])  →  [1,2,3]' },
  { name: "min(L) / max(L)", desc: "Smallest / largest element.", ex: 'max([5,2,9])  →  9' },
  { name: "sum(L)", desc: "Sum of all numeric items.", ex: 'sum([1,2,3])  →  6' },
];

const TUPLE_METHODS = [
  { name: "len(t)", desc: "Number of elements.", ex: 'len((1,2,3))  →  3' },
  { name: "tuple(it)", desc: "Convert iterable to tuple.", ex: 'tuple([1,2])  →  (1, 2)' },
  { name: "t.count(x)", desc: "How many times x appears.", ex: '(1,2,1).count(1)  →  2' },
  { name: "t.index(x)", desc: "Index of first x.", ex: '(10,20,30).index(20)  →  1' },
  { name: "sorted(t)", desc: "Return sorted LIST from tuple.", ex: 'sorted((3,1,2))  →  [1,2,3]' },
  { name: "min(t) / max(t)", desc: "Smallest / largest element.", ex: 'max((5,2,9))  →  9' },
  { name: "sum(t)", desc: "Sum of numeric items.", ex: 'sum((1,2,3))  →  6' },
  { name: "Tuple assignment", desc: "Unpack values to multiple variables.", ex: 'a, b = (10, 20)  →  a=10, b=20' },
  { name: "Nested tuple", desc: "Tuples inside tuples.", ex: 't = ((1,2), (3,4)); t[1][0]  →  3' },
];

const OPERATORS = [
  { name: "+  -  *  /", desc: "Add, subtract, multiply, divide (returns float).", ex: '10 / 3  →  3.333…' },
  { name: "//", desc: "Floor division — drops the fraction.", ex: '10 // 3  →  3' },
  { name: "%", desc: "Modulo — remainder.", ex: '10 % 3  →  1' },
  { name: "**", desc: "Exponent.", ex: '2 ** 10  →  1024' },
  { name: "==  !=", desc: "Equality / inequality.", ex: '5 == 5  →  True' },
  { name: ">  <  >=  <=", desc: "Comparison.", ex: '5 > 3  →  True' },
  { name: "and  or  not", desc: "Logical operators.", ex: 'True and False  →  False' },
  { name: "=  +=  -=  *=  /=  //=  %=  **=", desc: "Assignment / augmented assignment.", ex: 'x = 5; x += 2  →  x is 7' },
  { name: "is  is not", desc: "Identity — checks if SAME object in memory.", ex: 'a is None' },
  { name: "in  not in", desc: "Membership in sequence.", ex: '3 in [1,2,3]  →  True' },
];

const FLOW = [
  { name: "if / elif / else", desc: "Conditional branching.", ex: "if x > 0:\n    print('pos')\nelif x == 0:\n    print('zero')\nelse:\n    print('neg')" },
  { name: "for x in seq:", desc: "Iterate over a sequence.", ex: "for n in [1,2,3]:\n    print(n)" },
  { name: "range(start, stop, step)", desc: "Generate a sequence of integers.", ex: "list(range(1,6))  →  [1,2,3,4,5]" },
  { name: "while cond:", desc: "Repeat while condition is True.", ex: "while x < 5:\n    x += 1" },
  { name: "break", desc: "Exit the nearest loop immediately.", ex: "if found:\n    break" },
  { name: "continue", desc: "Skip to next iteration.", ex: "if n % 2 == 0:\n    continue" },
  { name: "Nested loops", desc: "Loop inside another loop.", ex: "for i in range(3):\n    for j in range(3):\n        print(i, j)" },
];

const DATA_TYPES = [
  { name: "int", desc: "Whole numbers.", ex: 'x = 42  →  type(x) is int' },
  { name: "float", desc: "Decimal numbers.", ex: 'pi = 3.14  →  type(pi) is float' },
  { name: "complex", desc: "Complex numbers.", ex: 'z = 2 + 3j  →  z.real, z.imag' },
  { name: "bool", desc: "True or False.", ex: '5 > 3  →  True' },
  { name: "str (string)", desc: "Sequence of characters. Immutable.", ex: '"brimi"' },
  { name: "list", desc: "Ordered, mutable sequence.", ex: '[1, 2, 3]' },
  { name: "tuple", desc: "Ordered, IMMUTABLE sequence.", ex: '(1, 2, 3)' },
  { name: "dict", desc: "Key → value mapping.", ex: '{"name": "Brimi", "year": 9}' },
  { name: "None", desc: "Represents absence of value.", ex: 'x = None' },
  { name: "Mutable vs Immutable", desc: "Lists/dicts can be changed; tuples/strings/numbers cannot.", ex: 's = "hi"; s[0] = "H"  →  TypeError' },
];

const IO_ERRORS = [
  { name: "input(prompt)", desc: "Read a line from console as STRING.", ex: 'name = input("Name? ")' },
  { name: "int(input(...))", desc: "Convert input to integer.", ex: 'age = int(input("Age? "))' },
  { name: "print(*objs, sep, end)", desc: "Display to console.", ex: 'print("Hi", "there", sep="-")' },
  { name: "Syntax error", desc: "Invalid Python grammar — caught BEFORE the code runs.", ex: 'print("hi"  ← missing )' },
  { name: "Run-time error", desc: "Crashes mid-execution.", ex: '10 / 0  →  ZeroDivisionError' },
  { name: "Logical error", desc: "Code runs but produces wrong result. Hardest to debug.", ex: 'avg = a + b / 2  (forgot parentheses!)' },
];

const FUNDAMENTALS = [
  { name: "# Comment", desc: "Single-line comment — Python ignores it.", ex: '# this is a note' },
  { name: '""" docstring """', desc: "Multi-line string, often used as documentation.", ex: '"""\nMy first program\n"""' },
  { name: "Indentation", desc: "Python uses INDENTS (4 spaces) to mark blocks — no { }.", ex: "if x > 0:\n    print('pos')" },
  { name: "Keywords", desc: "Reserved words you can't use as variable names.", ex: 'if, else, for, while, def, return, True, False, None' },
  { name: "Identifier rules", desc: "Letters/digits/_ — must start with a letter or _. Case-sensitive.", ex: 'age, _name, total2' },
  { name: "Variable", desc: "A name that refers to a value. Dynamically typed.", ex: 'x = 5; x = "hi"  (allowed)' },
  { name: "l-value vs r-value", desc: "LEFT of = stores; RIGHT produces a value.", ex: 'x  =  5 + 3   ←  l    r' },
  { name: "Interactive vs Script mode", desc: "REPL (one-line at a time) vs running a .py file.", ex: 'python my_program.py' },
];

const INTRO = [
  { name: "Download Python", desc: "Get the official installer from python.org. Always pick the latest 3.x.", ex: "https://www.python.org" },
  { name: "IDLE", desc: "Ships with Python. Simple, great for beginners.", ex: "Run: python -m idlelib" },
  { name: "PyCharm (Community)", desc: "Free IDE with smart autocomplete, debugger.", ex: "jetbrains.com/pycharm" },
  { name: "VS Code", desc: "Lightweight editor. Install Python extension.", ex: "code.visualstudio.com" },
  { name: "Spyder", desc: "Scientific Python IDE bundled with Anaconda.", ex: "spyder-ide.org" },
  { name: "Jupyter Notebook", desc: "Cell-based notebooks — great for learning step-by-step.", ex: "jupyter.org" },
  { name: ".py file extension", desc: "Python source files end with .py", ex: "hello.py" },
];

export const PYTHON_REF = {
  // Class XI
  "comp-11-9": { title: "Installing Python & Choosing an IDE", rows: INTRO },
  "comp-11-10": { title: "Python Fundamentals — Syntax, Tokens & Variables", rows: FUNDAMENTALS },
  "comp-11-11": { title: "Python Data Types", rows: DATA_TYPES },
  "comp-11-12": { title: "Input, Output & Error Types", rows: IO_ERRORS },
  "comp-11-13": { title: "Python Operators", rows: OPERATORS },
  "comp-11-14": { title: "Flow of Control", rows: FLOW },
  // Class XII
  "comp-12-9": { title: "Python Recap — Data Types, I/O, Operators, Flow", rows: [...DATA_TYPES, ...OPERATORS.slice(0, 5)] },
  "comp-12-10": { title: "String Methods", rows: STR_METHODS },
  "comp-12-11": { title: "List Methods", rows: LIST_METHODS },
  "comp-12-12": { title: "Tuple Methods", rows: TUPLE_METHODS },
};
